import { useEffect, useState } from 'react'
import { FileSignature, RefreshCw, Check } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useGetContractStatus, useRequestContract } from '../../hooks/backend/interviews'
import { type ContractStatus, contractStatusLabel, formatDate } from '../../utils/interviews'

export function ContractPanel({
  applicationId,
  canAct,
}: {
  applicationId: string
  canAct: boolean
}) {
  const { data, trigger: triggerStatus } = useGetContractStatus()
  const { trigger: triggerRequest } = useRequestContract()
  const [busy, setBusy] = useState(false)
  const [resendBusy, setResendBusy] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [justSent, setJustSent] = useState(false)

  const status = (data as ContractStatus | null) ?? null

  const fetchStatus = () => {
    triggerStatus({ application_id: applicationId }, { skipCache: true })
  }

  // Load on mount and whenever the selected applicant changes.
  useEffect(() => {
    fetchStatus()
    setJustSent(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await triggerStatus({ application_id: applicationId }, { skipCache: true }).result
    } finally {
      setRefreshing(false)
    }
  }

  // The DB function is asynchronous: after a successful trigger we notify the
  // user, then re-fetch once after a short delay so the new status appears.
  const afterRequest = () => {
    toast.success('Contract request sent')
    setTimeout(() => {
      triggerStatus({ application_id: applicationId }, { skipCache: true })
    }, 4000)
  }

  const handleSend = async () => {
    setBusy(true)
    try {
      await triggerRequest({ application_id: applicationId, mode: 'send' }).result
      setJustSent(true)
      afterRequest()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Could not send contract', { description: msg })
    } finally {
      setBusy(false)
    }
  }

  const handleResend = async () => {
    setResendBusy(true)
    try {
      await triggerRequest({ application_id: applicationId, mode: 'resend' }).result
      afterRequest()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Could not resend contract', { description: msg })
    } finally {
      setResendBusy(false)
    }
  }

  const isSigned = status?.status === 'signed'
  const isAwaiting = status?.status === 'sent'
  // Send is offered when there is no contract, or the latest is voided/declined.
  const canSendFresh =
    !status || status.status === 'voided' || status.status === 'declined'

  const relevantDate = isSigned ? status?.signed_at : isAwaiting ? status?.sent_at : null

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileSignature className="h-4 w-4 text-[hsl(var(--brand))]" />
          <h3 className="text-sm font-semibold text-foreground">Employment contract</h3>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="space-y-1.5">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Status</p>
        <p className="text-sm font-medium text-foreground">{contractStatusLabel(status)}</p>
        {relevantDate && (
          <p className="text-xs text-muted-foreground">
            {isSigned ? 'Signed on' : 'Sent on'} {formatDate(relevantDate)}
          </p>
        )}
      </div>

      {canAct && !isSigned && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          {justSent || isAwaiting ? (
            <div className="flex flex-col items-start gap-1">
              <Button
                variant="outline"
                disabled
                aria-disabled="true"
                className="h-11 w-full cursor-not-allowed justify-center opacity-100 sm:h-9 sm:w-auto"
              >
                <Check className="h-4 w-4" />
                Contract sent
              </Button>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendBusy}
                className="text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground disabled:opacity-50"
              >
                {resendBusy ? 'Resending…' : 'Resend contract'}
              </button>
            </div>
          ) : canSendFresh ? (
            <Button
              onClick={handleSend}
              disabled={busy}
              className="h-11 w-full justify-center sm:h-9 sm:w-auto"
            >
              {busy && <Spinner className="h-4 w-4" />}
              Send contract
            </Button>
          ) : null}
        </div>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        The driver receives a SignNow email with their details already filled in. They just review
        and sign.
      </p>
    </section>
  )
}
